import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  MailOutline as MailIcon,
  LoginOutlined as LoginIcon,
} from '@mui/icons-material';

interface RegistrationSuccessDialogProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onGoToLogin: () => void;
}

const RegistrationSuccessDialog: React.FC<RegistrationSuccessDialogProps> = ({
  open,
  email,
  onClose,
  onGoToLogin,
}) => {
  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const steps = [
    {
      label: '登録完了',
      description: 'アカウントの作成が完了しました',
      icon: <CheckIcon />,
    },
    {
      label: 'メール確認',
      description: '確認メールを送信しました',
      icon: <MailIcon />,
    },
    {
      label: 'ログイン',
      description: 'メール確認後、ログインできます',
      icon: <LoginIcon />,
    },
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          maxWidth: '600px',
          width: '90%',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CheckIcon sx={{ color: 'success.main', fontSize: 32 }} />
          <Typography variant="h5" component="span">
            登録が完了しました！
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{email}</strong> に確認メールを送信しました。
          </Typography>
        </Alert>

        <Stepper activeStep={1} orientation="vertical" sx={{ mb: 3 }}>
          {steps.map((step, index) => (
            <Step key={step.label} completed={index === 0}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: index === 0 ? 'success.main' : index === 1 ? 'primary.main' : 'grey.400',
                      color: 'white',
                    }}
                  >
                    {React.cloneElement(step.icon, { fontSize: 'small' })}
                  </Box>
                )}
              >
                {step.label}
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            次のステップ：
          </Typography>
          <Box component="ol" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2">
              メールボックスを確認してください
            </Typography>
            <Typography component="li" variant="body2">
              確認メール内のリンクをクリックしてください
            </Typography>
            <Typography component="li" variant="body2">
              メール確認後、ログインページからログインしてください
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            メールが届かない場合は、迷惑メールフォルダもご確認ください。
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onGoToLogin} 
          variant="contained" 
          fullWidth
          size="large"
        >
          ログインページへ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationSuccessDialog; 